// -----------------------------------------------------------------------------
//  Modello di prezzo delle fasce — UNICA fonte di verità lato frontend.
//
//  A database `tariff_band.pricing_mode` decide come si legge l'importo, e si
//  sceglie PER OGNI FASCIA: il backend trova la fascia in cui
//  cade la corsa e applica il suo modo, quindi un listino può essere 0–10 km a
//  fascia e 10–20 km al km.
//
//    flat                 prezzo della corsa         flat_price
//    per_km               € per ogni km              price_per_km × km
//    flat_plus_km_excess  fisso + €/km oltre la soglia
//                                                    flat_price + price_per_km × (km − min_km)
//    flat_plus_km_total   fisso + €/km su tutti i km
//                                                    flat_price + price_per_km × km
//
//  Il servizio decide solo il modo di PARTENZA: al km lo spostamento interno
//  (il runtime congela `route.applied_price_per_km`), a fascia gli altri.
//
//  Sbagliare il modo non dà errore: dà un prezzo plausibile e sbagliato. Il
//  backend rifiuta le fasce incoerenti, e `bandRowsProblem` lo dice prima.
// -----------------------------------------------------------------------------

const PER_KM_SERVICES = new Set(['journey']);

// Il servizio si paga a km invece che a corsa? È il modo PREDEFINITO.
export const isPerKmService = (service) => PER_KM_SERVICES.has(service);

export const MODES = [
  { value: 'flat', label: 'A fascia', hint: 'prezzo della corsa' },
  { value: 'per_km', label: 'Al km', hint: '€ per ogni km' },
  // I due «Fisso + €/km» differiscono solo nei km pagati. Fascia da 10 km,
  // 5 € + 1 €/km, corsa di 13,4 km: oltre la soglia 5 + 3,4 = 8,40 €, su tutti
  // i km 5 + 13,4 = 18,40 €.
  { value: 'flat_plus_km_excess', label: 'Fisso + €/km', hint: 'oltre la soglia' },
  { value: 'flat_plus_km_total', label: 'Fisso + €/km', hint: 'su tutti i km' },
];

export const defaultModeFor = (service) => (isPerKmService(service) ? 'per_km' : 'flat');

// Il modo ha una parte chilometrica in più rispetto al fisso?
export const isExcessMode = (mode) => mode === 'flat_plus_km_excess' || mode === 'flat_plus_km_total';

// Il modo di un listino esistente, letto dalle sue fasce: se sono tutte a km
// vale «al km», altrimenti «a fascia». Senza fasce, il predefinito del servizio.
export const modeOfBands = (service, bands) => {
  if (!bands || !bands.length) return defaultModeFor(service);
  return bands.every((b) => b.pricing_mode === 'per_km') ? 'per_km' : 'flat';
};

// Unità da mostrare in testata o accanto a un importo: è la sola cosa che
// distingue "0,80" inteso come 80 centesimi di corsa da 80 cent/km.
export const unitForMode = (mode) => (mode === 'per_km' ? '€/km' : '€ a corsa');
export const unitFor = (service, mode) => unitForMode(mode || defaultModeFor(service));
export const shortUnitForMode = (mode) => (mode === 'per_km' ? '€/km' : '€');

/**
 * Traduce una riga della BandTable nei tre campi che il backend si aspetta.
 *
 * @param {string} service   servizio della fascia (dà il modo predefinito)
 * @param {number} amount    importo scritto dall'utente in quella colonna
 * @param {number} excess    €/km oltre la soglia (modi «fisso + €/km»)
 * @param {string} [mode]    modo della RIGA
 */
export function bandPricing(service, amount, excess, mode) {
  const value = Number(amount ?? 0);
  const m = mode || defaultModeFor(service);

  if (m === 'per_km') {
    // per_km non ha parte fissa: l'importo È la tariffa chilometrica.
    return { pricing_mode: 'per_km', flat_price: 0, price_per_km: value };
  }

  const perKmExcess = Number(excess ?? 0);
  if (isExcessMode(m)) {
    return { pricing_mode: m, flat_price: value, price_per_km: perKmExcess };
  }
  // «A fascia» con un €/km già scritto: resta fisso + €/km.
  return {
    pricing_mode: perKmExcess ? 'flat_plus_km_excess' : 'flat',
    flat_price: value,
    price_per_km: perKmExcess,
  };
}

// Un importo davvero scritto: '' e null sono "non compilato", non zero.
export const filledAmount = (v) => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));
const filled = filledAmount;

// Il valore di una colonna, o quello della colonna di riferimento se è vuota:
// un campo svuotato in el-input vale '' e, passato così, diventerebbe 0 €.
export const amountOr = (value, fallback) => (filled(value) ? value : fallback);

/**
 * Il primo problema di un listino prima di salvarlo, o null. Stesse regole del
 * backend (`tariff-band.rules.ts`), dette in modo che si capisca cosa correggere.
 *
 * @param {Array} rows     righe della BandTable
 * @param {string} service servizio (dà il modo delle righe senza modo)
 * @param {object} [opts]
 * @param {boolean} [opts.twoOperators] il listino distingue 1 e 2 operatori
 */
export function bandRowsProblem(rows, service, { twoOperators = false } = {}) {
  for (const r of rows || []) {
    const open = r.max_km === null || r.max_km === undefined || r.max_km === '';
    const range = open ? `oltre ${r.min_km} km` : `${r.min_km}–${r.max_km} km`;
    const mode = r.mode || defaultModeFor(service);

    if (!filled(r.min_km) || Number(r.min_km) < 0) return `Fascia ${range}: indica da quanti km parte`;
    if (!open && !(Number(r.max_km) > Number(r.min_km))) {
      return `Fascia ${range}: il km finale deve essere maggiore di quello iniziale`;
    }
    if (!filled(r.price)) return `Fascia ${range}: manca il prezzo`;
    const amounts = [
      r.price, r.price2, r.price_share, r.price_share2,
      r.price_per_km, r.price2_per_km, r.price_share_per_km, r.price_share2_per_km,
    ];
    if (amounts.some((v) => filled(v) && Number(v) < 0)) {
      return `Fascia ${range}: gli importi non possono essere negativi`;
    }
    // Il preventivo del partner cerca la quota col numero di operatori della
    // corsa: con una sola delle due, l'altra corsa dice «tariffa non impostata».
    if (twoOperators && filled(r.price_share) !== filled(r.price_share2)) {
      return `Fascia ${range}: indica la quota partner sia dello spostamento a catena sia di quello singolo, oppure lasciale vuote entrambe`;
    }
    if (mode === 'per_km' && !(Number(r.price) > 0)) {
      return `Fascia ${range}: al km serve un prezzo per km maggiore di zero`;
    }
    if (isExcessMode(mode)) {
      if (!(Number(r.price_per_km) > 0)) {
        return `Fascia ${range}: indica i €/km oltre la soglia, oppure scegli «A fascia»`;
      }
      // vuoti valgono quelli della prima colonna; scritti, devono essere > 0
      // come vuole il backend
      if (filled(r.price2_per_km) && !(Number(r.price2_per_km) > 0)) {
        return `Fascia ${range}: i €/km dello spostamento singolo devono essere maggiori di zero`;
      }
      if ([r.price_share_per_km, r.price_share2_per_km].some((v) => filled(v) && !(Number(v) > 0))) {
        return `Fascia ${range}: i €/km della quota partner devono essere maggiori di zero`;
      }
    }
  }
  return null;
}

export default {
  MODES, isPerKmService, defaultModeFor, isExcessMode, modeOfBands,
  unitForMode, unitFor, shortUnitForMode, bandPricing, bandRowsProblem, amountOr, filledAmount,
};
